import React, { useState } from 'react';
import { Trash2, Search, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import './DataTable.css';

const DataTable = ({
  columns,
  data,
  onDelete,
  loading = false,
  itemsPerPage = 10,
  searchable = true,
  searchPlaceholder = "Search...",
  filters = null // New prop: array of filter objects { key, label, options }
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState({});

  // Apply filters
  const applyFilters = (data) => {
    let result = data;

    // Apply each active filter
    Object.keys(activeFilters).forEach(filterKey => {
      const filterValue = activeFilters[filterKey];
      if (filterValue && filterValue !== 'all') {
        result = result.filter(row => {
          const rowValue = row[filterKey];
          return rowValue && rowValue.toString().toLowerCase() === filterValue.toLowerCase();
        });
      }
    });

    return result;
  };

  // Filter data based on search term and filters
  let filteredData = applyFilters(data);

  filteredData = searchTerm
    ? filteredData.filter(row =>
        columns.some(col => {
          const value = row[col.key];
          return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
        })
      )
    : filteredData;

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleFilterChange = (filterKey, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  if (loading) {
    return (
      <div className="data-table-loading">
        <div className="data-table-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      {/* Search Bar and Filters */}
      <div className="data-table-controls">
        {searchable && (
          <div className="data-table-search-wrapper">
            <input
              type="text"
              className="data-table-search"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        )}

        {/* Filters */}
        {filters && filters.length > 0 && (
          <div className="data-table-filters">
            <Filter size={16} className="data-table-filter-icon" />
            {filters.map(filter => (
              <select
                key={filter.key}
                className="data-table-filter-select"
                value={activeFilters[filter.key] || 'all'}
                onChange={(e) => handleFilterChange(filter.key, e.target.value)}
              >
                <option value="all">{filter.label}: All</option>
                {filter.options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      {paginatedData.length === 0 ? (
        <div className="data-table-empty">
          <p>No records found</p>
        </div>
      ) : (
        <>
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} style={{ width: col.width }}>
                      {col.label}
                    </th>
                  ))}
                  {onDelete && <th style={{ width: '80px' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, rowIndex) => (
                  <tr key={row.id || rowIndex}>
                    {columns.map((col) => (
                      <td key={col.key}>
                        {col.render ? col.render(row[col.key], row) : row[col.key] || '-'}
                      </td>
                    ))}
                    {onDelete && (
                      <td>
                        <button
                          className="data-table-delete-btn"
                          onClick={() => onDelete(row)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="data-table-pagination">
              <div className="data-table-pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} entries
              </div>
              <div className="data-table-pagination-controls">
                <button
                  className="data-table-pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <span className="data-table-pagination-page">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="data-table-pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DataTable;
